import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ContactoApiService } from '../services/contacto-api.service';
import {
  Contact, ContactSearchParams, ContactState, ContactCycle, ContactOrigin,
  ContactAttributeDefinition, ContactHasAssociationsDetails, ContactAssociationsSummary,
  ContactBulkAction, ContactBulkActionResponse, ContactBulkActionFailure
} from '../models/contacto.model';
import { firstValueFrom, Observable, of, map, catchError } from 'rxjs';
import { EntitySelectComponent, EntitySelectPage } from '../../../shared/components/entity-select/entity-select.component';
import { CrmUserResponse } from '../../users/models/user.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ContactoDetailModalComponent } from '../contacto-detail-modal/contacto-detail-modal.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { EmailComposeService } from '../../correos/services/email-compose.service';
import { ImportExportModalComponent } from '../../../shared/components/ui/import-export-modal/import-export-modal.component';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { PermissionService } from '../../../shared/services/permission.service';
import { countryFromPhone, COUNTRIES_DATA } from '../../../shared/data/countries.data';
import { CountrySelectComponent } from '../../../shared/components/form/country-select/country-select.component';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-contacto-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    ContactoDetailModalComponent,
    ImportExportModalComponent,
    HasPermissionDirective,
    EntitySelectComponent,
    CountrySelectComponent
  ],
  templateUrl: './contacto-list.component.html'
})
export class ContactoListComponent implements OnInit {
  contactos: Contact[] = [];
  loading = false;
  error = '';

  /** Código ISO (minúscula) del país derivado del teléfono, para la bandera flag-icons. Null si no aplica. */
  countryFlagCode(phone?: string | null): string | null {
    return countryFromPhone(phone)?.code.toLowerCase() ?? null;
  }

  /** Nombre del país derivado del teléfono (para tooltip). */
  countryName(phone?: string | null): string {
    return countryFromPhone(phone)?.name ?? '';
  }

  /** Filtro por país: guarda el código elegido ('' = todos) y recarga. */
  onCountryFilterChange(code: string): void {
    this.filtroPais = code;
    this.buscar();
  }

  // Modal state
  showDetailModal = false;
  selectedContact: Contact | null = null;

  // Import/Export modal
  showImportExportModal = false;

  // Catálogos para filtros
  contactStates: ContactState[] = [];
  contactCycles: ContactCycle[] = [];
  contactOrigins: ContactOrigin[] = [];
  attributeDefinitions: ContactAttributeDefinition[] = [];

  // Filtro de responsable
  private readonly OWNER_FILTER_KEY = 'crm_owner_filter_contactos';
  /**
   * inc38 — 'unassigned' es la bandeja de contactos sin responsable. Los que entran por
   * formulario, widget de chat o página de reserva pueden quedarse sin dueño a propósito
   * (ver V179); sin este filtro no salían bajo "Mis contactos" de nadie y solo aparecían
   * perdidos entre "Todos".
   */
  filtroOwner: 'all' | 'mine' | 'unassigned' =
    (['mine', 'unassigned'].includes(localStorage.getItem(this.OWNER_FILTER_KEY) ?? '')
      ? localStorage.getItem(this.OWNER_FILTER_KEY) as 'mine' | 'unassigned'
      : 'all');

  // Filtro activo/inactivo: true = activos (defecto), false = inactivos
  filtroIsActive = true;

  // Filtros estándar
  showFilters = false;
  filtroNombre = '';
  filtroEmpresa = '';
  filtroContactState: number | undefined;
  filtroContactCycle: number | undefined;
  filtroOrigin: number | undefined;
  filtroPais = '';                          // código ISO del país (filtro por prefijo telefónico)
  readonly filterCountries = COUNTRIES_DATA;

  // Filtro por atributo custom (server-side, sin N+1)
  filtroAttributeDefinitionId: number | undefined;
  filtroAttrValue = '';

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'contacto-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  // ── Acciones masivas ──
  selectedContactIds: number[] = [];
  isBulkActionModalOpen = false;
  bulkAction: ContactBulkAction = 'CHANGE_OWNER';
  isExecutingBulk = false;
  bulkResult: ContactBulkActionResponse | null = null;
  // Responsable elegido para CHANGE_OWNER (vía EntitySelectComponent)
  bulkOwnerId: number | null = null;

  constructor(
    private contactoApi: ContactoApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private emailCompose: EmailComposeService,
    private permissions: PermissionService
  ) { }

  /** Abre el compose flotante global con el contacto precargado como destinatario. */
  enviarCorreo(contacto: Contact): void {
    if (!contacto.mail) {
      this.toast.error({ title: 'Sin correo', message: 'Este contacto no tiene una dirección de correo.' });
      return;
    }
    this.emailCompose.open({
      email: contacto.mail,
      fullName: contacto.fullname,
      contactId: contacto.id
    });
  }

  ngOnInit(): void {
    this.cargarCatalogos();
    this.cargarContactos();
  }

  /**
   * Permiso granular del módulo 'contacts' (Super Admin = true vía PermissionService).
   * Reemplaza el antiguo check por roleName.
   */
  get puedeImportar(): boolean {
    return this.permissions.canImport('contacts');
  }

  get puedeExportar(): boolean {
    return this.permissions.canExport('contacts');
  }

  /** El botón/modal se muestra si el usuario puede al menos una de las dos acciones. */
  get canImportExport(): boolean {
    return this.puedeImportar || this.puedeExportar;
  }

  abrirImportExport(): void {
    this.showImportExportModal = true;
  }

  cerrarImportExport(): void {
    this.showImportExportModal = false;
    this.cargarContactos();
  }

  private cargarCatalogos(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.contactoApi.getContactStates(businessId).subscribe({
      next: (data) => this.contactStates = data,
      error: () => { }
    });

    this.contactoApi.getContactCycles(businessId).subscribe({
      next: (data) => this.contactCycles = data,
      error: () => { }
    });

    this.contactoApi.getContactOrigins(businessId).subscribe({
      next: (data) => this.contactOrigins = data,
      error: () => { }
    });

    this.contactoApi.getAttributeDefinitions(businessId).subscribe({
      next: (data) => this.attributeDefinitions = data.filter(d => d.isActive),
      error: () => { }
    });
  }

  cargarContactos(): void {
    this.loading = true;
    this.error = '';
    // Cualquier recarga (paginación, filtro, búsqueda) invalida la selección previa.
    this.selectedContactIds = [];

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const params: ContactSearchParams = {
      businessId,
      searchTerm: this.filtroNombre || undefined,
      companySearchTerm: this.filtroEmpresa || undefined,
      idContactState: this.filtroContactState,
      idContactCycle: this.filtroContactCycle,
      idOrigin: this.filtroOrigin,
      phonePrefix: this.filtroPais
        ? COUNTRIES_DATA.find(c => c.code === this.filtroPais)?.dialCode || undefined
        : undefined,
      userPropietyId: this.filtroOwner === 'mine' ? user?.id : undefined,
      withoutOwner: this.filtroOwner === 'unassigned' ? true : undefined,
      isActive: this.filtroIsActive,
      page: this.globalPage,
      size: this.pageSize
    };

    // Agregar filtro de atributo custom si está activo
    const def = this.selectedAttrDef;
    if (def && this.filtroAttrValue !== '' && this.filtroAttrValue != null) {
      params.attributeDefinitionId = def.id;
      switch (def.type) {
        case 'text':    params.attrStrValue     = this.filtroAttrValue;              break;
        case 'list':    params.attrStrListValue  = this.filtroAttrValue;             break;
        case 'number':  params.attrIntValue      = Number(this.filtroAttrValue);     break;
        case 'boolean': params.attrBolValue      = this.filtroAttrValue === 'true';  break;
      }
    }

    this.contactoApi.searchContacts(params).subscribe({
      next: (response) => {
        this.contactos = response.content;
        this.globalPage = response.number ?? this.globalPage;
        this.globalTotalPages = response.totalPages;
        this.globalTotalElements = response.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar contactos';
        this.loading = false;
        console.error('Error:', err);
      }
    });
  }

  buscar(): void {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarContactos();
  }

  setOwnerFilter(value: 'all' | 'mine' | 'unassigned'): void {
    this.filtroOwner = value;
    this.filtroIsActive = true;
    localStorage.setItem(this.OWNER_FILTER_KEY, value);
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarContactos();
  }

  setInactivosFilter(): void {
    this.filtroOwner = 'all';
    this.filtroIsActive = false;
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarContactos();
  }

  limpiarFiltro(): void {
    this.filtroOwner = 'all';
    this.filtroIsActive = true;
    this.filtroNombre = '';
    this.filtroEmpresa = '';
    this.filtroContactState = undefined;
    this.filtroContactCycle = undefined;
    this.filtroOrigin = undefined;
    this.filtroPais = '';
    this.filtroAttributeDefinitionId = undefined;
    this.filtroAttrValue = '';
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarContactos();
  }

  /** Al cambiar la definición, limpiar el valor para evitar enviar params incorrectos. */
  onAttrDefChange(): void {
    this.filtroAttrValue = '';
    this.buscar();
  }

  nuevoContacto(): void {
    this.router.navigate(['/contactos/crear']);
  }

  vistaTablero(): void {
    this.router.navigate(['/contactos/kanban']);
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  editarContacto(contacto: Contact): void {
    this.router.navigate(['/contactos/editar', contacto.id]);
  }

  async restaurarContacto(contacto: Contact): Promise<void> {
    if (!contacto.id) return;
    const nombre = `${contacto.fullname ?? ''} ${contacto.lastname ?? ''}`.trim();

    const ok = await this.confirmDialog.confirm({
      title: 'Reactivar contacto',
      message: `¿Está seguro de reactivar al contacto "${nombre}"? Volverá a aparecer en la lista de contactos activos.`,
      variant: 'info',
      confirmText: 'Reactivar',
      cancelText: 'Cancelar'
    });
    if (!ok) return;

    this.contactoApi.restoreContact(contacto.id).subscribe({
      next: () => {
        this.cargarContactos();
        this.toast.success({ title: 'Contacto reactivado', message: `"${nombre}" ha sido reactivado correctamente.` });
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al reactivar el contacto' });
      }
    });
  }

  async archivarContacto(contacto: Contact): Promise<void> {
    if (!contacto.id) return;
    const nombre = `${contacto.fullname ?? ''} ${contacto.lastname ?? ''}`.trim();

    const ok = await this.confirmDialog.confirm({
      title: 'Inactivar contacto',
      message: `¿Está seguro de inactivar al contacto "${nombre}"? Una vez inactivo, el contacto no aparecerá en los procesos activos del sistema. Podrá reactivarlo o eliminarlo después.`,
      variant: 'warning',
      confirmText: 'Inactivar',
      cancelText: 'Cancelar'
    });
    if (!ok) return;

    this.contactoApi.archiveContact(contacto.id).subscribe({
      next: () => {
        this.cargarContactos();
        this.toast.success({ title: 'Contacto inactivado', message: `"${nombre}" ha sido inactivado correctamente.` });
      },
      error: (err) => {
        const msg = err.error?.message || 'Error al inactivar el contacto';
        this.confirmDialog.alert({
          title: 'No se puede inactivar el contacto',
          message: msg,
          variant: 'warning'
        });
      }
    });
  }

  async eliminarContacto(contacto: Contact): Promise<void> {
    if (!contacto.id) return;
    const nombre = `${contacto.fullname ?? ''} ${contacto.lastname ?? ''}`.trim();

    // Regla de negocio: solo se puede eliminar un contacto previamente inactivo.
    if (contacto.isActive !== false) {
      await this.confirmDialog.alert({
        title: 'No se puede eliminar el contacto',
        message: `El contacto "${nombre}" está activo. Para eliminarlo, primero debe ser inactivado. Solo se permite eliminar contactos inactivos.`,
        variant: 'warning'
      });
      return;
    }

    // Pre-check proactivo: mostrar qué asociaciones bloquean ANTES de pedir confirmación,
    // para no hacer confirmar un borrado que el backend va a rechazar con 409.
    let summary: ContactAssociationsSummary | undefined;
    try {
      summary = await firstValueFrom(this.contactoApi.getAssociationsSummary(contacto.id));
    } catch {
      // Si el summary falla, se intenta el delete igual; el handler de 409 abajo cubre el bloqueo.
    }
    if (summary?.hasAssociations) {
      await this.confirmDialog.alert({
        title: 'No se puede eliminar el contacto',
        message: `El contacto "${nombre}" no se puede eliminar porque tiene ${this.buildAssociationsBreakdown(summary)} asociado(s). `
          + `Debe desvincular o cerrar esos elementos antes de eliminarlo.`,
        variant: 'warning'
      });
      return;
    }

    const ok = await this.confirmDialog.confirm({
      title: 'Eliminar contacto',
      message: `¿Está seguro de eliminar al contacto "${nombre}"? Esta acción no se puede deshacer.`
        + this.buildWhatsappNotice(summary),
      variant: 'danger',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar'
    });
    if (!ok) return;

    this.contactoApi.deleteContact(contacto.id).subscribe({
      next: () => {
        this.cargarContactos();
        this.toast.deleted('Contacto');
      },
      error: (err) => {
        // 409 CONTACT_HAS_ASSOCIATIONS — puede ocurrir si se creó un registro entre el pre-check y el delete
        const details = err?.error?.details as ContactHasAssociationsDetails | undefined;
        if (err?.status === 409 && details?.error === 'CONTACT_HAS_ASSOCIATIONS') {
          const msg = err.error?.message || `No se puede eliminar: ${this.buildAssociationsBreakdown(details)}.`;
          this.toast.error({ title: 'No se puede eliminar', message: msg });
          return;
        }
        this.toast.error({ title: 'No se puede eliminar', message: err.error?.message || 'Error al eliminar el contacto' });
      }
    });
  }

  /**
   * Desvincula al contacto de TODAS sus campañas de marketing y empresas, luego lo elimina.
   * Solo disponible en la vista de inactivos. El backend sigue bloqueando (409) si quedan
   * asociaciones fuertes (deals/leads/quotes/meetings/tickets/tasks/formSubmissions).
   */
  async desvincularYEliminarContacto(contacto: Contact): Promise<void> {
    if (!contacto.id) return;
    const nombre = `${contacto.fullname ?? ''} ${contacto.lastname ?? ''}`.trim();

    // Este camino también elimina la ficha, así que debe avisar del historial igual que el otro.
    let summary: ContactAssociationsSummary | undefined;
    try {
      summary = await firstValueFrom(this.contactoApi.getAssociationsSummary(contacto.id));
    } catch {
      // Sin summary se sigue adelante: el aviso es informativo, no una condición del borrado.
    }

    const ok = await this.confirmDialog.confirm({
      title: 'Desvincular y eliminar',
      message: `Se quitará al contacto "${nombre}" de todas sus campañas de marketing y de sus empresas, y luego se eliminará de forma permanente. Esta acción no se puede deshacer. ¿Desea continuar?`
        + this.buildWhatsappNotice(summary),
      variant: 'warning',
      confirmText: 'Desvincular y eliminar',
      cancelText: 'Cancelar'
    });
    if (!ok) return;

    this.contactoApi.unlinkAndDeleteContact(contacto.id).subscribe({
      next: () => {
        this.cargarContactos();
        this.toast.deleted('Contacto');
      },
      error: (err) => {
        this.confirmDialog.alert({
          title: 'No se puede eliminar el contacto',
          message: err.error?.message || 'No se pudo eliminar el contacto',
          variant: 'warning'
        });
      }
    });
  }

  /**
   * Qué pasa con el historial de WhatsApp al borrar la ficha.
   *
   * La conversación y sus mensajes NO se destruyen —desde la V213 la FK es ON DELETE SET NULL—,
   * pero quedan sin contacto asociado, y eso hay que decirlo antes de confirmar: antes se
   * borraban en cascada y nadie llegaba a enterarse.
   */
  private buildWhatsappNotice(summary?: ContactAssociationsSummary): string {
    const conversaciones = summary?.whatsappConversations ?? 0;
    if (conversaciones <= 0) return '';

    const mensajes = summary?.whatsappMessages ?? 0;
    const hilos = conversaciones === 1 ? '1 conversación de WhatsApp' : `${conversaciones} conversaciones de WhatsApp`;
    const detalleMensajes = mensajes > 0
      ? ` y ${mensajes === 1 ? 'su mensaje' : `sus ${mensajes} mensajes`}`
      : '';
    return `\n\nSe conservarán ${hilos}${detalleMensajes}, que quedarán sin ficha asociada.`;
  }

  private buildAssociationsBreakdown(s: {
    companies: number; deals: number; leads: number; quotes: number;
    meetings: number; tickets: number; tasks: number;
    marketingCampaigns: number; formSubmissions: number;
  }): string {
    const parts: string[] = [];
    if (s.companies)          parts.push(`${s.companies} empresa(s)`);
    if (s.deals)              parts.push(`${s.deals} negocio(s)`);
    if (s.leads)              parts.push(`${s.leads} lead(s)`);
    if (s.quotes)             parts.push(`${s.quotes} cotización(es)`);
    if (s.meetings)           parts.push(`${s.meetings} reunión(es)`);
    if (s.tickets)            parts.push(`${s.tickets} ticket(s)`);
    if (s.tasks)              parts.push(`${s.tasks} tarea(s)`);
    if (s.marketingCampaigns) parts.push(`${s.marketingCampaigns} campaña(s) de marketing`);
    if (s.formSubmissions)    parts.push(`${s.formSubmissions} envío(s) de formulario`);
    return parts.join(', ');
  }

  // ──────────────────────────────────────────────
  // Selección múltiple
  // ──────────────────────────────────────────────

  toggleContactSelection(id?: number): void {
    if (id === undefined) return;
    const idx = this.selectedContactIds.indexOf(id);
    if (idx === -1) {
      this.selectedContactIds.push(id);
    } else {
      this.selectedContactIds.splice(idx, 1);
    }
  }

  isContactSelected(id?: number): boolean {
    return id !== undefined && this.selectedContactIds.includes(id);
  }

  toggleAllContacts(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedContactIds = checked
      ? this.contactos.map(c => c.id).filter((id): id is number => id !== undefined)
      : [];
  }

  get isAllContactsSelected(): boolean {
    return this.contactos.length > 0 && this.selectedContactIds.length === this.contactos.length;
  }

  // ──────────────────────────────────────────────
  // Acciones masivas
  // ──────────────────────────────────────────────

  openBulkActionModal(action: ContactBulkAction): void {
    this.bulkAction = action;
    this.bulkResult = null;
    this.bulkOwnerId = null;
    this.isBulkActionModalOpen = true;
  }

  closeBulkActionModal(): void {
    this.isBulkActionModalOpen = false;
    this.bulkResult = null;
    this.bulkOwnerId = null;
  }

  onBulkOwnerChange(userId: number | null): void {
    this.bulkOwnerId = userId;
  }

  /** Nombre que devuelve el backend en `failures[].name`; nunca el id. */
  getFailureName(f: ContactBulkActionFailure): string {
    return f.name?.trim() || 'Contacto sin nombre disponible';
  }

  getBulkActionLabel(action: ContactBulkAction): string {
    switch (action) {
      case 'CHANGE_OWNER': return 'Cambiar responsable';
      case 'ARCHIVE':      return 'Inactivar';
      case 'DELETE':       return 'Eliminar';
      default:             return action;
    }
  }

  ejecutarAccionMasiva(): void {
    if (this.selectedContactIds.length === 0) return;
    // CHANGE_OWNER exige un usuario elegido.
    if (this.bulkAction === 'CHANGE_OWNER' && !this.bulkOwnerId) return;

    this.isExecutingBulk = true;
    this.contactoApi.bulkActionContacts({
      action: this.bulkAction,
      contactIds: this.selectedContactIds,
      params: this.bulkAction === 'CHANGE_OWNER' ? { userPropietyId: this.bulkOwnerId! } : undefined
    }).subscribe({
      next: (res) => {
        this.bulkResult = res;
        this.isExecutingBulk = false;
        this.selectedContactIds = [];
        this.cargarContactos();

        if (res.failed === 0) {
          this.toast.success({
            title: this.getBulkActionLabel(this.bulkAction),
            message: `${res.processed} contacto(s) procesado(s) correctamente.`
          });
        } else {
          this.toast.info({
            title: 'Acción completada parcialmente',
            message: `${res.processed} procesado(s), ${res.failed} con error.`
          });
        }
      },
      error: (err) => {
        this.isExecutingBulk = false;
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudo ejecutar la acción masiva.'
        });
      }
    });
  }

  // ── Picker de responsable (CHANGE_OWNER) — client-side: el endpoint de usuarios no filtra por texto ──
  userSearch = (term: string): Observable<EntitySelectPage<CrmUserResponse>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.contactoApi.getUsersByBusiness(businessId).pipe(
      map((users) => {
        const t = term.toLowerCase();
        const items = t
          ? users.filter(u => `${u.fullname} ${u.lastname ?? ''} ${u.mail}`.toLowerCase().includes(t))
          : users;
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as CrmUserResponse[], totalPages: 0 }))
    );
  };

  userLabel = (u: CrmUserResponse): string => `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim();
  userSublabel = (u: CrmUserResponse): string => u.mail;

  // Métodos auxiliares para badges
  getContactStateName(id?: number): string {
    if (!id) return 'Sin Estado';
    const state = this.contactStates.find(s => s.id === id);
    return state?.name || 'Sin Estado';
  }

  getCycleName(id?: number): string {
    if (!id) return 'Sin ciclo';
    const cycle = this.contactCycles.find(c => c.id === id);
    return cycle?.name || 'Sin ciclo';
  }

  getOriginName(id?: number): string {
    if (!id) return 'Sin Origen';
    const origin = this.contactOrigins.find(o => o.id === id);
    return origin?.name || 'Sin Origen';
  }

  // Definición seleccionada actualmente
  get selectedAttrDef(): ContactAttributeDefinition | undefined {
    return this.attributeDefinitions.find(d => d.id === this.filtroAttributeDefinitionId);
  }

  parseListOptions(listOptions?: string | null): string[] {
    if (!listOptions) return [];
    try { return JSON.parse(listOptions); } catch { return []; }
  }

  // Paginación
  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.cargarContactos();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.buscar();
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }

  get hasPrevPage(): boolean { return this.globalPage > 0; }
  get hasNextPage(): boolean { return this.globalPage < this.globalTotalPages - 1; }
  get pageDisplay(): number  { return this.globalPage + 1; }

  verDetalle(contacto: Contact): void {
    this.selectedContact = contacto;
    this.showDetailModal = true;
  }

  cerrarModal(): void {
    this.showDetailModal = false;
    this.selectedContact = null;
  }
}
